// src/config/agentConfig.ts

import { createLogger } from "../utils/logger";

const log = createLogger("AgentConfig");

/**
 * Agent feature flags to control UI and behavior
 */
export interface AgentFeatures {
  // UI visibility controls
  /**
   * @deprecated Only MetricsPanel reads this, and that panel is retired - its
   * call site in App.tsx was commented out long before the tabs were removed.
   */
  showMetricsPanel: boolean;
  /**
   * @deprecated Moot. It never had a consumer (the Dashboard rendered off the
   * active tab, not this flag), and the Dashboard tab itself is now retired.
   */
  showDocumentStructure: boolean;
  showPromptSuggestions: boolean;

  // File upload capabilities
  allowFileUpload: boolean;
  supportedFileTypes?: string[];
  maxFileCount?: number;
  maxFileSizeMB?: number;

  // API endpoint configuration
  chatEndpoint: string;
  /**
   * @deprecated The `/summary/*` routes are no longer mounted on the backend,
   * and the Analysis tab that called them is gone. This string was always
   * documentation rather than routing (the real route is the switch in
   * agentAwareApiAdapter), so it is kept only as a record of where the
   * summary path used to point.
   */
  summaryEndpoint?: string;

  // User guidance
  tooltipMessage?: string;
  placeholderOverride?: string;
}

/**
 * Complete agent configuration including metadata and features
 */
export interface AgentConfiguration {
  id: string;
  name: string;
  description: string;
  features: AgentFeatures;
}

/**
 * Centralized agent configurations
 * This ensures consistent behavior across the application
 */
export const AGENT_CONFIGURATIONS: Record<string, AgentConfiguration> = {
  "ccm-space": {
    id: "ccm-space",
    name: "CCM Space",
    description: "General Purpose Agent",
    features: {
      // Similar to drafting assistant configuration
      showMetricsPanel: false,
      showDocumentStructure: true,
      showPromptSuggestions: false,
      allowFileUpload: true,
      supportedFileTypes: [".docx", ".xlsx", ".xls", ".pdf"],
      maxFileCount: 5,
      maxFileSizeMB: 25, // Reduced from 25MB as requested
      chatEndpoint: "/chat/general_agent/stream/", // Placeholder endpoint
      // No summary endpoint - disables Analysis tab
      tooltipMessage: "Upload context files to enhance your analysis and document generation",
      placeholderOverride: "Upload relevant files and ask me anything about your documents",
    },
  },

  "red-line-analyzer": {
    id: "red-line-analyzer",
    name: "Redline Analyzer",
    description: "Contract review and risk analysis",
    features: {
      // Enable file upload for additional context
      showMetricsPanel: true,
      showDocumentStructure: true, // TODO: Need a hook for this to show/hide
      showPromptSuggestions: true,
      allowFileUpload: true,
      supportedFileTypes: [".docx", ".xlsx", ".xls", ".pdf"],
      maxFileCount: 5,
      maxFileSizeMB: 25,
      chatEndpoint: "/chat/redline_analyzer/stream/",
      summaryEndpoint: "/summary/redline_analyzer/stream/",
      tooltipMessage: "Upload additional context file (Word or Excel) to enhance redline analysis",
      placeholderOverride:
        "Ask about the redlines or upload additional context for enhanced analysis",
    },
  },

  "contract-qa": {
    id: "contract-qa",
    name: "Contract Q&A",
    description: "Answer questions about contract terms",
    features: {
      // New behavior for Contract Q&A
      showMetricsPanel: false,
      showDocumentStructure: true, // Dashboard still available TODO: Need a hook for this to show/hide
      showPromptSuggestions: false,
      allowFileUpload: true,
      supportedFileTypes: [".docx", ".xlsx", ".xls", ".pdf"],
      maxFileCount: 5,
      maxFileSizeMB: 25,
      chatEndpoint: "/chat/executed_contracts/stream/",
      summaryEndpoint: "/summary/executed_contracts/stream/",
      tooltipMessage:
        "Upload standard contract templates using the plus icon to enable contract analysis",
      placeholderOverride: "Make sure you have your standard positions / template file uploaded",
    },
  },

  "drafting-assistant": {
    id: "drafting-assistant",
    name: "Drafting Assistant",
    description: "Contract writing and editing support",
    features: {
      // Drafting assistant specific configuration
      showMetricsPanel: false,
      showDocumentStructure: true, // TODO: Need a hook for this to show/hide
      showPromptSuggestions: false,
      allowFileUpload: true,
      supportedFileTypes: [".docx", ".xlsx", ".xls", ".pdf"],
      maxFileCount: 5, // Allow multiple context files
      maxFileSizeMB: 25,
      chatEndpoint: "/chat/document_generator/stream/",
      // No summary endpoint for drafting assistant
      tooltipMessage: "Upload context files to enhance document generation",
      placeholderOverride: "Add specs, SOW draft or Scope as context files for the best results",
    },
  },
};

/**
 * Get agent configuration by ID
 * Falls back to red-line-analyzer if agent not found
 */
export function getAgentConfig(agentId: string): AgentConfiguration {
  const config = AGENT_CONFIGURATIONS[agentId];

  if (!config) {
    log.warn(`Agent configuration not found for ID: ${agentId}, falling back to red-line-analyzer`);
    return AGENT_CONFIGURATIONS["red-line-analyzer"];
  }

  return config;
}

/**
 * Get agent features by ID
 * Convenience method for components that only need features
 */
export function getAgentFeatures(agentId: string): AgentFeatures {
  return getAgentConfig(agentId).features;
}

/**
 * Check if an agent supports file uploads
 */
export function agentSupportsFileUpload(agentId: string): boolean {
  return getAgentFeatures(agentId).allowFileUpload;
}

/**
 * Get API endpoints for an agent
 */
export function getAgentEndpoints(agentId: string): {
  chat: string;
  summary?: string;
} {
  const features = getAgentFeatures(agentId);
  return {
    chat: features.chatEndpoint,
    summary: features.summaryEndpoint,
  };
}
